import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { chapterTickObstacles, liftChapterTickPose } from "./clearance";
import { chapterTickPoses, chapterTicks } from "./marks";
import { timeJumpTicks, timeMarkTicks } from "./timeMarks";

/** The most a label takes: it can wrap to two lines. */
const LABEL_HEIGHT_PX = 60;
/** How far from the playhead a label is still on screen. */
const ON_SCREEN_PX = 900;

interface LabelTop {
  x: number;
  topY: number;
}

const cache = new WeakMap<BookTimelineLayout, { width: number; tops: LabelTop[] }>();

function labelTops(layout: BookTimelineLayout, viewportWidth: number): LabelTop[] {
  const known = cache.get(layout);
  if (known?.width === viewportWidth) {
    return known.tops;
  }
  // Time labels too, so the words for a jump back are on screen before it happens.
  const ticks = layout.journey === "universe"
    ? timeMarkTicks(layout)
    : [...chapterTicks(layout.beats), ...timeJumpTicks(layout)];
  const tops = chapterTickPoses(ticks, layout.rail.branches, viewportWidth).map((base) => {
    const obstacles = chapterTickObstacles(layout, base.startMs, viewportWidth);
    const { x, y } = liftChapterTickPose(base, obstacles);
    return { x, topY: y - LABEL_HEIGHT_PX };
  });
  cache.set(layout, { width: viewportWidth, tops });
  return tops;
}

/** The highest point of any chapter label near this spot, so the camera can keep it in frame. */
export function highestLabelNear(
  layout: BookTimelineLayout,
  worldX: number,
  viewportWidth: number,
): number | null {
  const near = labelTops(layout, viewportWidth)
    .filter((label) => Math.abs(label.x - worldX) < ON_SCREEN_PX);
  return near.length === 0 ? null : Math.min(...near.map((label) => label.topY));
}
